import { CSSProperties } from 'react';

export const PRINTING_TECHNIQUES_WITH_TEXT_FOIL = ['CUSTOM_FOIL', 'LETTERPRESS_FOIL'] as const;
type PrintingTechniqueWithTextFoil = (typeof PRINTING_TECHNIQUES_WITH_TEXT_FOIL)[number];

export const printingTechniqueSupportsTextFoil = (
  printingTechnique: string | undefined
): printingTechnique is PrintingTechniqueWithTextFoil => {
  return PRINTING_TECHNIQUES_WITH_TEXT_FOIL.includes(
    printingTechnique as PrintingTechniqueWithTextFoil
  );
};

const FOIL_COLORS = ['copper', 'gold', 'matte-black', 'rose-gold', 'silver', 'white'] as const;
type FoilColorWithStyle = (typeof FOIL_COLORS)[number];

const isFoilColorWithStyle = (foilColor: string | undefined): foilColor is FoilColorWithStyle => {
  return FOIL_COLORS.includes(foilColor as FoilColorWithStyle);
};

type FoilProperties = {
  color: string;
  backgroundImage: string;
};

const FOIL_STYLES: Record<FoilColorWithStyle, FoilProperties> = {
  gold: {
    color: 'd0b554',
    backgroundImage:
      'linear-gradient(to right, #d0b554 4%, #b6951a 15%, #e4bb26 31%, #cea40d 77%, #ffd427)',
  },
  silver: {
    color: 'c0bebe',
    backgroundImage: 'linear-gradient(to right, #c4c4c4 4%, #8e8e8e 37%, #eeeeee 74%, #cacaca 99%)',
  },
  copper: {
    color: 'dda99a',
    backgroundImage:
      'linear-gradient(to right, #dda194 4%, #f1d6c4 37%, #efcfbe 70%, #dda99a 100%)',
  },
  'rose-gold': {
    color: 'dda99a',
    backgroundImage:
      'linear-gradient(to right, #dda194 4%, #f1d6c4 37%, #efcfbe 70%, #dda99a 100%)',
  },
  'matte-black': {
    color: '121111',
    backgroundImage:
      'linear-gradient(to right, #616161 4%, #121111 37%, #414141 74%, #616161 100%)',
  },
  white: {
    color: 'f2f7f9',
    backgroundImage: 'linear-gradient(to right, #f2f7f9 4%, #dbe7ec 30%, #ffffff 70%, #f2f7f9 90%)',
  },
};

export const getPrintedTextColor = (
  color: string,
  printingTechnique: string | undefined,
  foilColor: string | undefined
) => {
  if (isFoilColorWithStyle(foilColor) && printingTechniqueSupportsTextFoil(printingTechnique)) {
    return FOIL_STYLES[foilColor]?.color || color;
  }
  return color;
};

export const getFoilCssProperties = (
  foilColor: string | undefined,
  printingTechnique: string | undefined
): CSSProperties => {
  if (isFoilColorWithStyle(foilColor) && printingTechniqueSupportsTextFoil(printingTechnique)) {
    const foilData = FOIL_STYLES[foilColor];
    return {
      WebkitTextFillColor: 'transparent',
      WebkitBackgroundClip: 'text',
      backgroundImage: foilData.backgroundImage,
    };
  }
  return {};
};
